// File: src/layouts/GuestLayout.jsx

import React, {
  useState,
  useEffect,
  useCallback,
} from "react";

import { Outlet } from "react-router-dom";
import axios from "axios";

import { EventsContext } from "./StandardLayout";
import NavBarWithAuth from "../components/NavBarWithAuth";

const MARKET_KEY = "tribe_selected_market";
const MARKET_EVENT = "tribe:market-selected";

const API_URL = (
  process.env.REACT_APP_API_URL || ""
).replace(/\/+$/, "");

function normalizeMarketId(value) {
  const id = String(value?._id ?? value ?? "");

  return /^[a-fA-F0-9]{24}$/.test(id)
    ? id.toLowerCase()
    : "";
}

function readSavedMarket() {
  try {
    const saved = JSON.parse(
      localStorage.getItem(MARKET_KEY) || "null"
    );

    return {
      marketId: normalizeMarketId(saved?.marketId),
      label:
        typeof saved?.label === "string"
          ? saved.label
          : "",
    };
  } catch {
    return { marketId: "", label: "" };
  }
}

export default function GuestLayout({ children }) {
  const [selection, setSelection] = useState(readSavedMarket);

  const [eventResults, setEventResults] = useState({
    marketId: "",
    events: [],
  });

  const [isLoadingEvents, setIsLoadingEvents] = useState(false);
  const [eventsError, setEventsError] = useState("");
  const [retryCount, setRetryCount] = useState(0);

  const selectedMarketId = selection.marketId;
  const selectedMarketLabel = selection.label;

  const handleMarketSelected = useCallback((picked) => {
    const marketId = normalizeMarketId(picked?.marketId);

    if (!marketId) return;

    const nextSelection = {
      marketId,
      label: picked.label || picked.market?.name || "",
    };

    setSelection(nextSelection);
    setEventResults({ marketId, events: [] });
    setEventsError("");
    setIsLoadingEvents(true);

    try {
      localStorage.setItem(
        MARKET_KEY,
        JSON.stringify(nextSelection)
      );
    } catch {
      // Browsing still works when storage is unavailable.
    }

    // Refresh even when the same market is selected again.
    setRetryCount((count) => count + 1);
  }, []);

  // Support market pickers elsewhere on the guest page,
  // including any desktop navigation rendered by a child.
  useEffect(() => {
    function handleExternalSelection(event) {
      const marketId = normalizeMarketId(
        event.detail?.marketId
      );

      if (!marketId) return;

      setSelection({
        marketId,
        label:
          event.detail?.label ||
          event.detail?.market?.name ||
          "",
      });
    }

    function handleStorageChange(event) {
      if (event.key === MARKET_KEY || event.key === null) {
        setSelection(readSavedMarket());
      }
    }

    window.addEventListener(
      MARKET_EVENT,
      handleExternalSelection
    );

    window.addEventListener(
      "storage",
      handleStorageChange
    );

    return () => {
      window.removeEventListener(
        MARKET_EVENT,
        handleExternalSelection
      );

      window.removeEventListener(
        "storage",
        handleStorageChange
      );
    };
  }, []);

  // Fetch events for the selected market.
  // Ignore stale responses after a market change or unmount.
  useEffect(() => {
    let active = true;

    setEventResults({
      marketId: selectedMarketId,
      events: [],
    });

    setEventsError("");

    if (!selectedMarketId) {
      setIsLoadingEvents(false);
      return undefined;
    }

    setIsLoadingEvents(true);

    async function fetchMarketEvents() {
      try {
        const res = await axios.get(
          `${API_URL}/events`,
          {
            params: {
              marketId: selectedMarketId,
            },
          }
        );

        if (!Array.isArray(res.data)) {
          throw new Error("Unable to load gatherings.");
        }

        if (active) {
          setEventResults({
            marketId: selectedMarketId,
            events: res.data,
          });
        }
      } catch (err) {
        if (!active) return;

        console.error(
          "[GuestLayout] Loading market events failed:",
          err
        );

        setEventsError(
          err.response?.data?.message ||
            "Unable to load gatherings for this city."
        );
      } finally {
        if (active) {
          setIsLoadingEvents(false);
        }
      }
    }

    fetchMarketEvents();

    return () => {
      active = false;
    };
  }, [selectedMarketId, retryCount]);

  const refreshEvents = useCallback(() => {
    setRetryCount((count) => count + 1);
  }, []);

  // Never show another market's results during a switch.
  const marketEvents =
    eventResults.marketId === selectedMarketId
      ? eventResults.events
      : [];

  const content = children || <Outlet />;

  return (
    <EventsContext.Provider
      value={{
        marketEvents,
        selectedMarketId,
        selectedMarketLabel,
        isLoadingEvents,
        eventsError,
        refreshEvents,
        onMarketSelected: handleMarketSelected,

        // Compatibility until event-list consumers are updated.
        nearbyEvents: marketEvents,
        searchActive: true,
      }}
    >
      <div className="guest-page" style={styles.guestPage}>
        <style>{`
          .guest-page > .guest-fixed-nav {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            z-index: 1000;
            background: #fff;
          }

          .guest-page > .guest-page-content {
            padding-top: 52px;
          }

          /*
           * Landing page only:
           * allow the hero to start behind the navbar.
           */
          .guest-page:has(.tribeCarouselHero) > .guest-fixed-nav {
            position: absolute;
            background: transparent;
          }

          .guest-page:has(.tribeCarouselHero) > .guest-page-content {
            padding-top: 0;
          }

          @media (max-width: 440px) {
            .guest-page .tribeCarouselHero {
              min-height: 100svh;
              padding-top: 104px;
              padding-bottom: 76px;
            }
          }

          @media (min-width: 441px) {
            .guest-page > .guest-fixed-nav {
              display: none !important;
            }

            .guest-page > .guest-page-content {
              padding-top: 0 !important;
            }
          }
        `}</style>

        <div className="guest-fixed-nav">
          <NavBarWithAuth
            selectedMarketId={selectedMarketId}
            onMarketSelected={handleMarketSelected}
          />
        </div>

        <main
          className="guest-page-content"
          style={styles.content}
        >
          {content}
        </main>
      </div>
    </EventsContext.Provider>
  );
}

const styles = {
  guestPage: {
    position: "relative",
    width: "100%",
    minHeight: "100vh",
    background: "#fff",
    overflowX: "hidden",
  },

  content: {
    width: "100%",
    position: "relative",
  },
};